import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './Button.tsx';
import { cx } from './cx.ts';

/**
 * Page-based pagination strip. Position readout is tabular mono
 * (`current / total`, DESIGN.md §3); labels arrive translated from the
 * caller.
 *
 * The source console-web version leaned on an `IconButton` primitive
 * that this app deliberately does not carry — the arrows are ghost
 * `Button`s instead, carrying both `aria-label` (§9: icon-only controls
 * always name themselves) and `title`, so pointer users keep the native
 * hover hint `IconButton` used to give them.
 */
interface Props {
  readonly page: number;
  readonly pageCount: number;
  readonly onPageChange: (page: number) => void;
  readonly prevLabel: string;
  readonly nextLabel: string;
  readonly ariaLabel: string;
  readonly className?: string;
  readonly testId?: string;
}

export function Pagination({
  page,
  pageCount,
  onPageChange,
  prevLabel,
  nextLabel,
  ariaLabel,
  className,
  testId,
}: Props): JSX.Element {
  return (
    <nav
      aria-label={ariaLabel}
      data-testid={testId}
      className={cx('flex items-center gap-2', className)}
    >
      <Button
        variant="ghost"
        size="sm"
        aria-label={prevLabel}
        title={prevLabel}
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        className="px-1.5"
      >
        <ChevronLeft aria-hidden="true" className="h-4 w-4" />
      </Button>
      <span className="font-mono text-mono tabular-nums text-ink-mid">
        {page} / {Math.max(pageCount, 1)}
      </span>
      <Button
        variant="ghost"
        size="sm"
        aria-label={nextLabel}
        title={nextLabel}
        disabled={page >= pageCount}
        onClick={() => onPageChange(page + 1)}
        className="px-1.5"
      >
        <ChevronRight aria-hidden="true" className="h-4 w-4" />
      </Button>
    </nav>
  );
}
